// src/components1/CategoryTrendDrawer.js
import React, { useRef, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

/**
 * CategoryTrendDrawer:
 * Renders an expandable 12-month trend chart for a category.
 * Enables smooth horizontal scrolling between the previous 6 months and the next 6 months.
 */
const CategoryTrendDrawer = ({ cat, trendData = [], currency = "$", gradId, activeYearMonth = null }) => {
  const scrollRef = useRef(null);
  const tableRef = useRef(null);
  // the table opens straight away when a single month is being viewed: that is
  // the month the report is already filtered to
  const [view, setView] = useState(activeYearMonth ? "table" : "chart");
  // the month whose individual spends are listed; the one being viewed starts open
  const [openMonth, setOpenMonth] = useState(activeYearMonth);

  const totalAmount = trendData.reduce((acc, m) => acc + (m.amount || 0), 0);
  const monthlyAvg = totalAmount / (trendData.length || 1);
  const selectedRow = activeYearMonth
    ? trendData.find((m) => m.yearMonth === activeYearMonth)
    : null;

  useEffect(() => {
    setOpenMonth(activeYearMonth);
  }, [activeYearMonth]);

  const fmtDay = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { day: "numeric", month: "short" });
  };

  // bring the month being viewed into sight rather than making them scroll for it
  useEffect(() => {
    if (view !== "table" || !activeYearMonth || !tableRef.current) return;
    const row = tableRef.current.querySelector('[data-active="true"]');
    if (row && row.scrollIntoView) {
      row.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [view, activeYearMonth]);

  // Auto-scroll to the right on mount so the next / recent 6 months are visible initially
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            left: scrollRef.current.scrollWidth,
            behavior: "smooth",
          });
        }
      }, 60);
    }
  }, []);

  const scrollToPrev = (e) => {
    e.stopPropagation();
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  };

  const scrollToNext = (e) => {
    e.stopPropagation();
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  const chartWidth = Math.max(560, trendData.length * 48);

  return (
    <div className="category-trend-drawer mt-3 pt-3" onClick={(e) => e.stopPropagation()}>
      {/* Top Drawer Header with Trend Title & Quick 6M Jump Controls */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-1">
          <i className="fi fi-rr-chart-line-up" style={{ color: cat.color, fontSize: "14px" }}></i>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--primary-dark)" }}>
            Monthly Trend
          </span>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: "6px",
              background: "rgba(66, 34, 74, 0.08)",
              color: "var(--primary-deep)",
              marginLeft: "4px",
            }}
          >
            {trendData.length} Months
          </span>
        </div>

        {/* 6-Month Jump Buttons & Metrics */}
        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            onClick={scrollToPrev}
            style={{
              background: "#FAF7FB",
              border: "1px solid var(--border-subtle)",
              borderRadius: "8px",
              padding: "2px 7px",
              fontSize: "10px",
              fontWeight: 600,
              color: "var(--primary-deep)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
            }}
            title="Scroll left to see previous 6 months"
          >
            <i className="fi fi-rr-arrow-small-left"></i> Prev 6M
          </button>

          <button
            type="button"
            onClick={scrollToNext}
            style={{
              background: "rgba(212, 85, 42, 0.1)",
              border: "1px solid rgba(212, 85, 42, 0.25)",
              borderRadius: "8px",
              padding: "2px 7px",
              fontSize: "10px",
              fontWeight: 600,
              color: "var(--accent-coral)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
            }}
            title="Scroll right to see next 6 months"
          >
            Next 6M <i className="fi fi-rr-arrow-small-right"></i>
          </button>

          <span
            style={{
              fontSize: "10.5px",
              fontWeight: 600,
              background: "rgba(66, 34, 74, 0.08)",
              color: "var(--primary-deep)",
              padding: "2px 7px",
              borderRadius: "8px",
              marginLeft: "4px",
            }}
          >
            Avg: {currency}{monthlyAvg.toFixed(0)}/mo
          </span>

          <div className="cat-view-switch" role="group" aria-label="Trend view">
            <button
              type="button"
              className={view === "chart" ? "active" : ""}
              onClick={(e) => { e.stopPropagation(); setView("chart"); }}
            >
              <i className="fi fi-rr-chart-line-up"></i> Chart
            </button>
            <button
              type="button"
              className={view === "table" ? "active" : ""}
              onClick={(e) => { e.stopPropagation(); setView("table"); }}
            >
              <i className="fi fi-rr-list"></i> Table
            </button>
          </div>
        </div>
      </div>

      {view === "chart" ? (
        <>
        {/* Horizontally Scrollable 12-Month Area Chart */}
        <div
          ref={scrollRef}
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "4px",
            scrollbarWidth: "none",
          }}
        >
          <div style={{ width: `${chartWidth}px`, height: 160, position: "relative" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 15, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={cat.color} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={cat.color} stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ECE5EE" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "var(--text-muted)", fontWeight: 500 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "var(--text-muted)" }}
                  tickFormatter={(v) => `${currency}${v}`}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const item = payload[0].payload;
                      return (
                        <div
                          style={{
                            background: "var(--primary-dark)",
                            color: "#FFFFFF",
                            padding: "6px 10px",
                            borderRadius: "8px",
                            fontSize: "11.5px",
                            boxShadow: "0 4px 12px rgba(18, 2, 22, 0.2)",
                          }}
                        >
                          <div style={{ fontWeight: 600, color: "rgba(255, 255, 255, 0.75)" }}>
                            {item.month} {item.yearMonth ? `(${item.yearMonth})` : ""}
                          </div>
                          <div style={{ fontWeight: 700, color: "#FFFFFF", fontSize: "13px" }}>
                            {currency}
                            {Number(item.amount).toFixed(2)}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke={cat.color}
                  strokeWidth={3}
                  fillOpacity={1}
                  fill={`url(#${gradId})`}
                  dot={{ fill: cat.color, stroke: "#FFFFFF", strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: cat.color, stroke: "#FFFFFF", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Month-by-Month Mini Badges aligned to scroll width */}
          <div
            className="d-flex gap-1 justify-content-between mt-2 pt-2 border-top"
            style={{ width: `${chartWidth}px` }}
          >
            {trendData.map((m, mIdx) => (
              <div
                key={mIdx}
                className="text-center"
                style={{
                  flex: 1,
                  padding: "4px 2px",
                  background: m.amount > 0 ? "rgba(66, 34, 74, 0.05)" : "transparent",
                  borderRadius: "6px",
                }}
              >
                <div
                  style={{
                    fontSize: "9.5px",
                    color: "var(--text-muted)",
                    fontWeight: 500,
                    lineHeight: 1.1,
                  }}
                >
                  {m.month}
                </div>
                <div
                  style={{
                    fontSize: "10.5px",
                    fontWeight: 700,
                    color: m.amount > 0 ? "var(--primary-dark)" : "var(--text-muted)",
                  }}
                >
                  {m.amount > 0 ? `${currency}${Math.round(m.amount)}` : "-"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Swipe cue hint */}
        <div className="d-flex justify-content-between align-items-center mt-2 px-1" style={{ fontSize: "10px", color: "var(--text-muted)" }}>
          <span>
            <i className="fi fi-rr-arrow-small-left me-1"></i> Swipe to see previous months
          </span>
          <span>
            Scroll right for next 6 months <i className="fi fi-rr-arrow-small-right ms-1"></i>
          </span>
        </div>
        </>
      ) : (
        <>
          {/* the month the report is filtered to stays put while the table scrolls */}
          {selectedRow && (
            <div className="cat-month-selected">
              <span>
                {selectedRow.month} {selectedRow.yearMonth?.slice(0, 4)}
                <span className="cat-month-items">
                  {selectedRow.count || 0} {selectedRow.count === 1 ? "item" : "items"}
                </span>
              </span>
              <strong>{currency}{Number(selectedRow.amount).toFixed(2)}</strong>
            </div>
          )}

          <div className="cat-month-table-wrap" ref={tableRef}>
          <table className="cat-month-table">
            <thead>
              <tr>
                <th>Month</th>
                <th className="num">Items</th>
                <th className="num">Amount</th>
                <th className="num">Share</th>
              </tr>
            </thead>
            <tbody>
              {trendData.map((m, i) => {
                const share = totalAmount > 0 ? (m.amount / totalAmount) * 100 : 0;
                const isActive = !!activeYearMonth && m.yearMonth === activeYearMonth;
                const hasItems = (m.items || []).length > 0;
                const isOpen = hasItems && openMonth === m.yearMonth;

                return (
                  <React.Fragment key={i}>
                    <tr
                      data-active={isActive ? "true" : "false"}
                      className={`${isActive ? "is-active" : ""} ${m.amount > 0 ? "" : "is-empty"} ${hasItems ? "is-openable" : ""} ${isOpen ? "is-open" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasItems) setOpenMonth((prev) => (prev === m.yearMonth ? null : m.yearMonth));
                      }}
                    >
                      <td>
                        {isActive && <span className="cat-month-dot" style={{ background: cat.color }}></span>}
                        {m.month} <span className="cat-month-year">{m.yearMonth?.slice(2, 4)}</span>
                        {hasItems && (
                          <i className={`fi fi-rr-angle-small-${isOpen ? "up" : "down"} cat-month-caret`}></i>
                        )}
                      </td>
                      <td className="num">{m.count ? m.count : "-"}</td>
                      <td className="num">
                        {m.amount > 0 ? `${currency}${Number(m.amount).toFixed(2)}` : "-"}
                      </td>
                      <td className="num">{m.amount > 0 ? `${share.toFixed(0)}%` : "-"}</td>
                    </tr>

                    {/* each spend in that month, not just its total */}
                    {isOpen &&
                      m.items.map((item) => (
                        <tr key={item.id} className="cat-month-item">
                          <td colSpan={2}>
                            <span className="cat-item-day">{fmtDay(item.date)}</span>
                            <span className="cat-item-desc">{item.description}</span>
                            {item.paidBy && <span className="cat-item-payer">{item.paidBy.split("@")[0]}</span>}
                          </td>
                          <td className="num" colSpan={2}>
                            {currency}
                            {item.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{trendData.reduce((n, m) => n + (m.count || 0), 0) || "-"}</td>
                <td className="num">{currency}{totalAmount.toFixed(2)}</td>
                <td className="num">100%</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryTrendDrawer;
